import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Check,
  ArrowRight,
  Eye,
  Save,
  ChevronRight,
  Search,
  Users,
  CloudCheck,
  CloudOff,
} from "lucide-react";
import {
  type Bootstrap,
  type Draft,
  type DocumentRecord,
  type Snapshot,
  type Kind,
  type Client,
  totals,
  money,
  addDays,
  kindLabels,
  dateLabel,
  validationErrors,
} from "../../shared/domain";
import { makePdf, pdfFile, downloadPdf } from "../pdf";
import { api, ApiError, errorMessage } from "../api";
import { Field, AmountInput, Spinner, Notice, Modal } from "./Common";
export type Review = {
  snapshot: Snapshot;
  token: string;
  expires: string;
  version: number;
};
import { recoveries, saveRecovery, removeRecovery } from "../recovery";
import { DraftSaver } from "../draft-saver";
export function Editor({
  initial,
  data,
  onClose,
  onSaved,
  onIssued,
  onCustomer,
  onCopy,
  addedCustomer,
}: {
  initial: Draft;
  data: Bootstrap;
  onClose: () => void;
  onSaved: (d: DocumentRecord) => void;
  onIssued: (d: DocumentRecord) => void;
  onCustomer: () => void;
  onCopy: (draft: Draft) => void;
  addedCustomer: Client | null;
}) {
  const [draftPdf, setDraftPdf] = useState<File | null>(null);
  const [draft, setDraft] = useState(() => {
    try {
      const saved = recoveries(data.user.email).find(
        (d) => d.id === initial.id,
      );
      if (saved) return saved;
    } catch {}
    return initial;
  });
  const [review, setReview] = useState<Review | null>(null),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [fields, setFields] = useState<Record<string, string>>({}),
    [message, setMessage] = useState(""),
    [checked, setChecked] = useState(false),
    [exit, setExit] = useState(false);
  const [picker, setPicker] = useState(false),
    [customerSearch, setCustomerSearch] = useState(""),
    [localSaved, setLocalSaved] = useState(true),
    [syncing, setSyncing] = useState(false),
    [syncError, setSyncError] = useState(""),
    [conflict, setConflict] = useState(draft.version !== initial.version),
    [retry, setRetry] = useState(0),
    [removedLine, setRemovedLine] = useState<{
      line: Draft["lines"][number];
      index: number;
    } | null>(null);
  const editScroll = useRef(0);
  const customerTrigger = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null),
    mounted = useRef(true),
    savedCallback = useRef(onSaved),
    [saver] = useState(() => {
      const writer = new DraftSaver(
        { ...initial, version: draft.version },
        (value) => api<DocumentRecord>(`/documents/${value.id}`, "PUT", value),
        (value, record) => {
          try {
            if (writer.dirty) saveRecovery(data.user.email, value);
            else removeRecovery(data.user.email, value.id);
          } catch {}
          savedCallback.current(record);
          if (mounted.current) setDraft(value);
        },
      );
      writer.update(draft);
      return writer;
    });
  savedCallback.current = onSaved;
  const dirty = saver.dirty;
  useEffect(() => {
    const guard = (event: Event) => {
      if (saver.dirty && !localSaved) {
        event.preventDefault();
        setExit(true);
      }
    };
    window.addEventListener("ailink:beforeleave", guard);
    return () => window.removeEventListener("ailink:beforeleave", guard);
  }, [saver, localSaved]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!dirty) return;
    try {
      saveRecovery(data.user.email, draft);
      setLocalSaved(true);
      setMessage(
        navigator.onLine
          ? "Saved on this device · waiting to sync"
          : "Saved on this device · waiting for connection",
      );
    } catch {
      setLocalSaved(false);
      setMessage(
        "Device storage is unavailable. Save your draft before leaving.",
      );
    }
  }, [draft, dirty, data.user.email]);
  useEffect(() => {
    const online = () => {
      setSyncError("");
      setRetry((n) => n + 1);
    };
    const prevent = (e: BeforeUnloadEvent) => {
      if (saver.dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("online", online);
    window.addEventListener("beforeunload", prevent);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("beforeunload", prevent);
    };
  }, [saver]);
  useEffect(() => {
    if (!dirty || busy || review || conflict || syncError || !navigator.onLine)
      return;
    const timer = setTimeout(() => {
      void persist(false).catch(() => {});
    }, 1800);
    return () => clearTimeout(timer);
  }, [draft, dirty, busy, review, conflict, syncError, retry]);
  const change = (patch: Partial<Draft>) => {
    const next = { ...saver.current, ...patch };
    saver.update(next);
    setDraft(next);
    setReview(null);
    setChecked(false);
    setError("");
    setFields({});
  };
  const updateLine = (index: number, patch: Partial<Draft["lines"][0]>) =>
    change({
      lines: saver.current.lines.map((l, i) =>
        i === index ? { ...l, ...patch } : l,
      ),
    });
  async function persist(drain = true) {
    if (conflict)
      throw new Error(
        "This draft changed on another device. Keep your edits as a new draft.",
      );
    setSyncing(true);
    setSyncError("");
    try {
      const record = await saver.flush(drain);
      if (mounted.current)
        setMessage(
          saver.dirty
            ? "Saved on this device · waiting to sync"
            : "Saved to your shared workspace",
        );
      return record;
    } catch (e) {
      if (mounted.current) {
        setSyncError(errorMessage(e));
        if (e instanceof ApiError && e.status === 409) setConflict(true);
      }
      throw e;
    } finally {
      if (mounted.current) setSyncing(false);
    }
  }
  useEffect(() => {
    if (addedCustomer) selectCustomer(addedCustomer.id);
  }, [addedCustomer?.id]);
  function selectCustomer(id: string) {
    const c = data.clients.find((x) => x.id === id);
    change({
      clientId: id,
      dueDate: addDays(
        draft.issueDate || data.today,
        c?.defaultPaymentTermDays ?? 1,
      ),
    });
    setPicker(false);
    setCustomerSearch("");
  }
  async function save(thenReview = false) {
    if (thenReview) {
      const errors = validationErrors(
        saver.current,
        data.clients.find((c) => c.id === saver.current.clientId),
      );
      if (Object.keys(errors).length) {
        setFields(errors);
        setError("Check the highlighted details before reviewing.");
        requestAnimationFrame(() =>
          form.current
            ?.querySelector<HTMLElement>(
              ".invalid input, .invalid textarea, .invalid select, .invalid button",
            )
            ?.focus(),
        );
        return;
      }
      if (!form.current?.reportValidity()) return;
    }
    if (thenReview) editScroll.current = window.scrollY;
    setBusy(thenReview ? "review" : "save");
    setError("");
    setFields({});
    try {
      const d = await persist();
      if (thenReview) {
        const r = await api<Review>(`/documents/${d.id}/preview`, "POST", {
          version: d.version,
        });
        setDraftPdf(null);
        setReview(r);
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    } catch (e) {
      setError(errorMessage(e));
      if (e instanceof ApiError) setFields(e.fields);
    } finally {
      setBusy("");
    }
  }
  function editDetails() {
    setReview(null);
    setChecked(false);
    requestAnimationFrame(() => window.scrollTo(0, editScroll.current));
  }
  async function issue() {
    if (!review || !checked) return;
    setBusy("issue");
    setError("");
    try {
      const doc = await api<DocumentRecord>(
        `/documents/${draft.id}/issue`,
        "POST",
        { version: review.version, token: review.token },
      );
      removeRecovery(data.user.email, draft.id);
      onIssued(doc);
    } catch (e) {
      setError(errorMessage(e));
      if (e instanceof ApiError && e.status === 409) setChecked(false);
    } finally {
      setBusy("");
    }
  }
  const t = totals(draft),
    customer = data.clients.find((c) => c.id === draft.clientId);
  return (
    <div className={`editor page-narrow ${review ? "reviewing" : "composing"}`}>
      <button
        className="back"
        onClick={() =>
          review ? editDetails() : dirty ? setExit(true) : onClose()
        }
        disabled={!!busy}
      >
        <ArrowLeft size={18} />
        {review ? "Edit details" : "Invoices"}
      </button>
      <div className="page-title">
        <div>
          <h1>
            {review
              ? "Review document"
              : `${initial.version > 0 ? "Edit" : "New"} ${kindLabels[draft.kind].toLowerCase()}`}
          </h1>
        </div>
        <span className="badge draft">Draft</span>
      </div>
      {(conflict || syncError) && (
        <Notice type="error">
          {conflict
            ? "This draft changed on another device. Your edits are still here; keep them as a new draft to avoid replacing someone else’s work."
            : syncError}
          {conflict ? (
            <button
              className="text-button"
              onClick={() =>
                onCopy({ ...draft, id: crypto.randomUUID(), version: 0 })
              }
            >
              Keep edits as a new draft
            </button>
          ) : (
            <button
              className="text-button"
              disabled={syncing}
              onClick={() => {
                setSyncError("");
                setRetry((n) => n + 1);
              }}
            >
              Retry saving
            </button>
          )}
        </Notice>
      )}
      {error && (
        <Notice type="error">
          {error}
          {error.includes("another device") && (
            <button className="text-button" onClick={onClose}>
              Return to the latest records
            </button>
          )}
        </Notice>
      )}
      {review ? (
        <>
          <div className="review-summary">
            <div>
              <small>Customer</small>
              <strong>{customer?.businessName}</strong>
            </div>
            <div>
              <small>Total including VAT</small>
              <strong>{money(t.totalPence)}</strong>
            </div>
            <div>
              <small>Issue date</small>
              <span>{dateLabel(draft.issueDate)}</span>
            </div>
            <div>
              <small>Due date</small>
              <span>{dateLabel(draft.dueDate)}</span>
            </div>
          </div>
          <div className="preview-label">
            <h2>Document preview</h2>
            <button
              className="text-button"
              disabled={!draftPdf}
              onClick={() => {
                if (draftPdf) downloadPdf(draftPdf);
              }}
            >
              Download draft PDF <Eye size={16} />
            </button>
          </div>
          <InvoicePreview snapshot={review.snapshot} onReady={setDraftPdf} />
          <p className="muted small">
            The final number is assigned when you issue. The draft watermark
            will be removed.
          </p>
          <label className="check-row">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>
              I’ve checked the customer, service dates, prices and VAT.
            </span>
          </label>
          <div className="action-bar">
            <button
              className="secondary"
              onClick={editDetails}
              disabled={!!busy}
            >
              Edit details
            </button>
            <button
              className="primary"
              onClick={issue}
              disabled={!checked || !!busy || !draftPdf}
            >
              {busy === "issue" ? <Spinner /> : <Check size={18} />}{" "}
              {busy === "issue"
                ? "Issuing…"
                : `Issue ${kindLabels[draft.kind].toLowerCase()}`}
            </button>
          </div>
        </>
      ) : (
        <form
          ref={form}
          onSubmit={(e) => {
            e.preventDefault();
            void save(true);
          }}
          noValidate
        >
          <section className="form-section">
            <div className="section-heading">
              <span className="step-number">01</span>
              <h2>Customer & document</h2>
            </div>
            <Field label="Document type">
              <select
                value={draft.kind}
                onChange={(e) =>
                  change({
                    kind: e.target.value as Kind,
                    amountPaidPence: 0,
                    paymentReceivedDate: "",
                  })
                }
              >
                {Object.entries(kindLabels).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <div className={`field ${fields.clientId ? "invalid" : ""}`}>
              <span id="customer-label">
                Customer <span aria-hidden="true">*</span>
              </span>
              <button
                type="button"
                className="customer-select"
                ref={customerTrigger}
                aria-labelledby="customer-label customer-value"
                aria-haspopup="dialog"
                aria-invalid={!!fields.clientId}
                onClick={() => setPicker(true)}
              >
                <Users size={19} />
                <span id="customer-value">
                  {customer?.businessName || "Choose a saved customer"}
                </span>
                <ChevronRight size={18} />
              </button>
              {fields.clientId && (
                <small className="field-error">{fields.clientId}</small>
              )}
            </div>
            {customer && (
              <p className="customer-address">
                {[
                  customer.billingAddress.line1,
                  customer.billingAddress.city,
                  customer.billingAddress.postcode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
            <button type="button" className="text-button" onClick={onCustomer}>
              <Plus size={16} />
              Add a customer
            </button>
            <div className="two-columns">
              <Field label="Issue date" error={fields.issueDate} required>
                <input
                  type="date"
                  value={draft.issueDate}
                  required
                  onChange={(e) => change({ issueDate: e.target.value })}
                />
              </Field>
              <Field
                label={draft.kind === "quotation" ? "Valid until" : "Due date"}
                error={fields.dueDate}
                required
              >
                <input
                  type="date"
                  value={draft.dueDate}
                  min={draft.issueDate}
                  required
                  onChange={(e) => change({ dueDate: e.target.value })}
                />
              </Field>
            </div>
            <details open={draft.customerReference ? true : undefined}>
              <summary>
                Customer reference <span className="muted">Optional</span>
              </summary>
              <Field
                label="Customer reference"
                hint="Optional purchase order or booking reference"
              >
                <input
                  value={draft.customerReference}
                  maxLength={150}
                  onChange={(e) =>
                    change({ customerReference: e.target.value })
                  }
                  placeholder="e.g. PO-1042"
                />
              </Field>
            </details>
          </section>
          <section className="form-section">
            <div className="section-heading">
              <span className="step-number">02</span>
              <div>
                <h2>Services</h2>
                <p>Enter each amount before VAT.</p>
              </div>
            </div>
            {draft.lines.map((line, i) => (
              <div className="service" key={line.id} data-service-id={line.id}>
                <div className="service-heading">
                  <h3>Service {String(i + 1).padStart(2, "0")}</h3>
                  {draft.lines.length > 1 && (
                    <button
                      type="button"
                      className="icon-button danger-text"
                      aria-label={`Remove service ${i + 1}`}
                      onClick={() => {
                        setRemovedLine({ line, index: i });
                        change({
                          lines: draft.lines.filter((_, n) => n !== i),
                        });
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
                <Field
                  label="Description"
                  required
                  error={fields[`line.${i}.description`]}
                >
                  <textarea
                    rows={2}
                    value={line.description}
                    maxLength={600}
                    required
                    placeholder="e.g. Delivery service CF10"
                    onChange={(e) =>
                      updateLine(i, { description: e.target.value })
                    }
                  />
                </Field>
                {!line.description && customer?.defaultServiceDescription && (
                  <button
                    type="button"
                    className="service-suggestion text-button"
                    onClick={() =>
                      updateLine(i, {
                        description: customer.defaultServiceDescription,
                      })
                    }
                  >
                    Use “{customer.defaultServiceDescription}”
                  </button>
                )}
                <div className="two-columns">
                  <Field
                    label="Service date"
                    required
                    error={fields[`line.${i}.supplyDate`]}
                  >
                    <input
                      type="date"
                      value={line.supplyDate}
                      required
                      onChange={(e) =>
                        updateLine(i, { supplyDate: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Amount before VAT"
                    required
                    error={fields[`line.${i}.netPence`]}
                  >
                    <AmountInput
                      value={line.netPence}
                      required
                      onChange={(netPence) => updateLine(i, { netPence })}
                    />
                  </Field>
                </div>
              </div>
            ))}
            <button
              className="secondary add-service"
              type="button"
              disabled={draft.lines.length >= 30}
              onClick={() => {
                const id = crypto.randomUUID();
                flushSync(() =>
                  change({
                    lines: [
                      ...draft.lines,
                      {
                        id,
                        description: "",
                        supplyDate: draft.issueDate || data.today,
                        netPence: 0,
                      },
                    ],
                  }),
                );
                const field = form.current?.querySelector<HTMLTextAreaElement>(
                  `[data-service-id="${id}"] textarea`,
                );
                field?.focus({ preventScroll: true });
                field?.scrollIntoView({
                  block: "center",
                  behavior: matchMedia("(prefers-reduced-motion: reduce)")
                    .matches
                    ? "instant"
                    : "smooth",
                });
              }}
            >
              <Plus size={18} />
              Add another service
            </button>
          </section>
          {removedLine && (
            <div className="undo-notice" role="status">
              <span>Service removed</span>
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  const lines = [...draft.lines];
                  lines.splice(
                    Math.min(removedLine.index, lines.length),
                    0,
                    removedLine.line,
                  );
                  change({ lines });
                  setRemovedLine(null);
                }}
                disabled={draft.lines.length >= 30}
              >
                Undo
              </button>
            </div>
          )}
          <section className="form-section">
            <div className="section-heading">
              <span className="step-number">03</span>
              <h2>VAT & payment</h2>
            </div>
            <Field label="VAT rate">
              <select
                value={draft.vatBasisPoints}
                onChange={(e) =>
                  change({ vatBasisPoints: Number(e.target.value) })
                }
              >
                <option value={2000}>20% — standard rate</option>
                <option value={500}>5% — reduced rate</option>
                <option value={0}>0% — zero rate</option>
                {![0, 500, 2000].includes(draft.vatBasisPoints) && (
                  <option value={draft.vatBasisPoints}>
                    {draft.vatBasisPoints / 100}%
                  </option>
                )}
              </select>
            </Field>
            <div className="totals">
              <div>
                <span>Subtotal</span>
                <span>{money(t.netPence)}</span>
              </div>
              <div>
                <span>VAT ({draft.vatBasisPoints / 100}%)</span>
                <span>{money(t.vatPence)}</span>
              </div>
              <div className="grand-total">
                <strong>Total</strong>
                <strong>{money(t.totalPence)}</strong>
              </div>
            </div>
            {draft.kind === "invoice" && (
              <details>
                <summary>Payment already received?</summary>
                <div className="two-columns">
                  <Field label="Amount received" error={fields.amountPaidPence}>
                    <AmountInput
                      value={draft.amountPaidPence}
                      onChange={(amountPaidPence) =>
                        change({ amountPaidPence })
                      }
                    />
                  </Field>
                  <Field
                    label="Payment date"
                    error={fields.paymentReceivedDate}
                  >
                    <input
                      type="date"
                      max={data.today}
                      value={draft.paymentReceivedDate}
                      onChange={(e) =>
                        change({ paymentReceivedDate: e.target.value })
                      }
                    />
                  </Field>
                </div>
              </details>
            )}
            <details>
              <summary>Internal note</summary>
              <Field
                label="Note"
                hint="Only visible in your workspace. Not printed on the PDF."
              >
                <textarea
                  rows={3}
                  value={draft.internalNote}
                  maxLength={2000}
                  onChange={(e) => change({ internalNote: e.target.value })}
                />
              </Field>
            </details>
          </section>
          <div className="action-bar compose-actions">
            <div className="action-summary">
              <span>Total including VAT</span>
              <strong>{money(t.totalPence)}</strong>
              <span className="save-status" role="status">
                {syncing ? (
                  <>
                    <Spinner /> Saving…
                  </>
                ) : conflict || syncError ? (
                  <>
                    <CloudOff size={14} />{" "}
                    {localSaved ? "Saved on this device" : "Not saved · retry"}
                  </>
                ) : dirty ? (
                  message
                ) : (
                  <>
                    <CloudCheck size={14} />{" "}
                    {draft.version > 0
                      ? "Saved to your shared workspace"
                      : "Ready when you are"}
                  </>
                )}
              </span>
            </div>
            <button
              type="button"
              className="secondary save-draft"
              disabled={!!busy || syncing || conflict}
              onClick={() => void save()}
              aria-label="Save draft"
            >
              <Save size={17} />
              <span>Save draft</span>
            </button>
            <button
              className="primary"
              type="submit"
              disabled={!!busy || conflict}
            >
              {busy === "review" ? <Spinner /> : <ArrowRight size={18} />}Review
              document
            </button>
          </div>
        </form>
      )}
      {picker && (
        <Modal
          title="Choose a customer"
          onClose={() => setPicker(false)}
          returnFocus={customerTrigger}
          sheet
        >
          <label className="search picker-search">
            <Search size={18} />
            <input
              type="search"
              placeholder="Search name or postcode"
              aria-label="Search saved customers"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </label>
          <div className="picker-list">
            {(() => {
              const recent = [
                ...new Set(data.documents.map((d) => d.draft.clientId)),
              ]
                .filter(Boolean)
                .slice(0, 5);
              const matches = data.clients.filter((c) =>
                `${c.businessName} ${c.billingAddress.postcode}`
                  .toLowerCase()
                  .includes(customerSearch.trim().toLowerCase()),
              );
              const ordered = customerSearch
                ? matches
                : [...matches].sort((a, b) => {
                    const ai = recent.indexOf(a.id),
                      bi = recent.indexOf(b.id);
                    return (
                      (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi) ||
                      a.businessName.localeCompare(b.businessName)
                    );
                  });
              return ordered.length ? (
                ordered.map((c) => (
                  <button
                    type="button"
                    className="picker-option"
                    key={c.id}
                    onClick={() => selectCustomer(c.id)}
                    aria-pressed={draft.clientId === c.id}
                  >
                    <span>
                      <strong>{c.businessName}</strong>
                      <small>
                        {[c.billingAddress.city, c.billingAddress.postcode]
                          .filter(Boolean)
                          .join(" · ")}
                        {!customerSearch && recent.includes(c.id)
                          ? " · Recent"
                          : ""}
                      </small>
                    </span>
                    {draft.clientId === c.id ? (
                      <Check size={20} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </button>
                ))
              ) : (
                <p className="picker-empty">
                  No matching customers. Try a different name or add a customer.
                </p>
              );
            })()}
          </div>
          <button
            type="button"
            className="secondary picker-add"
            onClick={() => {
              setPicker(false);
              onCustomer();
            }}
          >
            <Plus size={18} />
            Add a customer
          </button>
        </Modal>
      )}
      {exit && (
        <Modal title="Keep this draft?" onClose={() => setExit(false)}>
          <p>
            {localSaved
              ? "Your latest changes are saved on this device. Save them to the shared workspace to open them on another phone."
              : "Device storage is unavailable. Save to the shared workspace before leaving to keep your changes."}
          </p>
          <div className="dialog-actions">
            <button
              className="secondary"
              onClick={onClose}
              disabled={!localSaved}
            >
              Keep on this device
            </button>
            <button
              className="primary"
              disabled={!!busy}
              onClick={async () => {
                setBusy("save");
                try {
                  await persist();
                  onClose();
                } catch (e) {
                  setError(errorMessage(e));
                  setExit(false);
                } finally {
                  setBusy("");
                }
              }}
            >
              Save & close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
function InvoicePreview({
  snapshot,
  onReady,
}: {
  snapshot: Snapshot;
  onReady: (file: File) => void;
}) {
  const [pages, setPages] = useState<string[]>([]),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setPages([]);
    setError("");
    void makePdf(snapshot, true)
      .then((result) => {
        if (cancelled) return;
        setPages(result.previews);
        onReady(pdfFile(result.bytes, "Draft-invoice.pdf"));
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [snapshot, onReady, attempt]);
  if (error)
    return (
      <div>
        <Notice type="error">{error}</Notice>
        <button
          className="secondary inline"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Retry preview
        </button>
      </div>
    );
  if (!pages.length)
    return (
      <p role="status">
        <Spinner /> Preparing your document preview…
      </p>
    );
  return (
    <div className="vector-preview" aria-label="Draft invoice preview">
      {pages.map((svg, i) => (
        <div key={i} dangerouslySetInnerHTML={{ __html: svg }} />
      ))}
    </div>
  );
}
